// The commissioned puzzle pieces (tools/puzzle-art.py → public/assets/puzzle/puzzle.{png,json}),
// placeholder art until the artist's: one 24 px frame per key. FixtureLayer draws a piece from here
// where the atlas has it and falls back to its vector drawing where it does not. These are the
// choices it makes: which frame a fixture wears, and how it is turned.
import type { Tile } from '../../core/sim/types';

export const PUZZLE_ATLAS = { key: 'puzzle', png: '/assets/puzzle/puzzle.png', json: '/assets/puzzle/puzzle.json' } as const;

/** The atlas a frame lives in: Oryx's world or items sheet, else the puzzle pieces. */
export function atlasOf(frame: string): string {
  if (frame.startsWith('item_')) return 'items';
  if (frame.startsWith('world_')) return 'world';
  return PUZZLE_ATLAS.key;
}

export type Side = 'N' | 'E' | 'S' | 'W';
const SIDES: readonly Side[] = ['N', 'E', 'S', 'W'];
const STEP: Record<Side, readonly [number, number]> = { N: [0, -1], E: [1, 0], S: [0, 1], W: [-1, 0] };
/** The channel pieces, each with the sides it opens on as drawn (unturned). */
const CHANNEL_PIECES: readonly (readonly [string, readonly Side[]])[] = [
  ['channel', ['W', 'E']], ['channel-corner', ['W', 'S']], ['channel-t', ['W', 'E', 'S']],
  ['channel-cross', ['N', 'E', 'S', 'W']], ['channel-end', ['W']],
];

/** The sides of `t` that water reaches across: a neighbouring square in `water`. */
export function openSides(t: Tile, water: readonly Tile[]): Side[] {
  return SIDES.filter((s) => water.some((w) => w.x === t.x + STEP[s][0] && w.y === t.y + STEP[s][1]));
}

/** The piece and quarter turns clockwise that open a channel square on `open` (alone, it runs east–west). */
export function channelPiece(open: readonly Side[]): { key: string; turns: number } {
  const want = new Set<Side>(open.length ? open : ['W', 'E']);
  for (const [key, sides] of CHANNEL_PIECES) {
    if (sides.length !== want.size) continue;
    for (let k = 0; k < 4; k++) {
      if (sides.every((s) => want.has(SIDES[(SIDES.indexOf(s) + k) % 4]!))) return { key, turns: k };
    }
  }
  return { key: 'channel', turns: 0 };
}

/** Statues: Oryx's hooded, archer and knight, and the commissioned angel, stag, heron, lion, dragon and queen. */
const STATUES = [
  'world_0376', 'statue-angel', 'statue-stag', 'statue-heron', 'world_0378', 'world_0379', 'statue-lion', 'statue-dragon', 'statue-queen',
] as const;
const STATUE_WORDS: readonly (readonly [RegExp, number])[] = [
  [/angel|saint|seraph|winged|martyr/i, 1],
  [/lion|griffin|cat|leopard/i, 6],
  [/dragon|wyrm|wyvern|serpent|drake/i, 7],
  [/stag|hart|deer|elk|boar|bull|ram|wolf|bear|fox|hound|beast|horse/i, 2],
  [/heron|crane|stork|bird|owl|eagle|raven|hawk|swan|crow|rook/i, 3],
  [/archer|hunter|ranger|bow/i, 4],
  [/queen|lady|maiden|mother|nun/i, 8],
  [/knight|king|warrior|lord|soldier/i, 5],
];

/** The frame a statue wears: by what it is carved as, else picked by its name. */
export function statueFrame(name: string): string {
  const head = name.split(' — ')[0]!;
  for (const [re, i] of STATUE_WORDS) if (re.test(head)) return STATUES[i]!;
  let h = 0;
  for (const ch of head) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return STATUES[h % STATUES.length]!;
}

/** A lever's frame and whether it is mirrored (thrown the other way): winch, waking, warden's or plain. */
export function leverArt(name: string, on: boolean): { key: string; flip: boolean } {
  const head = name.split(' — ')[0]!;
  if (/^the winch /.test(head)) return { key: on ? 'winch-wound' : 'winch', flip: false };
  if (/^the waking /.test(head)) return { key: 'lever-waking', flip: on };
  if (/ warden's /.test(head)) return { key: 'lever-warden', flip: on };
  return { key: on ? 'lever-up' : 'lever', flip: false };
}

const MOONS: Record<string, string> = { 'new moon': 'moon-new', 'waxing moon': 'moon-waxing', 'full moon': 'moon-full', 'waning moon': 'moon-waning' };
const FILLS = ['empty', 'a quarter full', 'half full', 'three quarters full', 'full'];

/** docs/106: a moon dial or an hourglass shows its face as a picture; other dials show marks. */
export function dialArt(face: string): string | undefined {
  if (MOONS[face]) return MOONS[face];
  const q = FILLS.indexOf(face);
  return q >= 0 ? `hourglass-${q}` : undefined;
}

/** Looks whose `on` has a frame of its own (a dyed thread). */
export const ON_FRAMES: Record<string, string> = { loom: 'loom-dyed', flagstone: 'flagstone-flagged' };

const CREATURES: readonly (readonly [RegExp, string])[] = [
  [/heron|crane|stork/i, 'wonder-heron'], [/stag|hart|deer|elk/i, 'wonder-stag'], [/fox|vixen/i, 'wonder-fox'],
  [/owl/i, 'wonder-owl'], [/serpent|snake|adder|viper|eel/i, 'wonder-serpent'], [/moth|butterfly/i, 'wonder-moth'],
  [/tortoise|turtle/i, 'wonder-tortoise'], [/toad|frog/i, 'wonder-toad'], [/horse|mare|pony|colt/i, 'horse'],
];

/** docs/108: a ferry passenger named for a creature ("the heron") rides as that creature. */
export function creatureArt(name: string): string | undefined {
  const head = name.split(' — ')[0]!;
  return CREATURES.find(([re]) => re.test(head))?.[1];
}

/** docs/108: a Nim heap as big as its count (none left: no stones). */
export function cairnArt(stones: number): string | undefined {
  if (stones <= 0) return undefined;
  return stones <= 2 ? 'cairn-small' : stones <= 5 ? 'cairn' : 'cairn-large';
}
